import { useForm } from 'react-hook-form';
import { usePreservedCallback } from '@toss/react';
import { TextField } from '@zep-us/zus';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { type YoutubeVideoData } from '@/api/ai/youtube/types';
import { useEffectOnce } from '@/hooks';
import { XIcon, YoutubeFillIcon } from '@/icons';
import { alertDialog, Button, IconButton } from '@/ui_v3';
import { cn } from '@/utils';
import { validateYoutubeUrl } from '@/views/build/util/youtubeUrlValidator';
export const YoutubeUrlTextField = ({
  videoData,
  onSearch,
  onCancel
}: {
  videoData: YoutubeVideoData | null;
  onSearch: (videoId: string, videoUrl: string) => void;
  onCancel: () => void;
}) => {
  const {
    getValues,
    reset,
    watch,
    register,
    formState,
    setFocus
  } = useForm({
    defaultValues: {
      url: videoData?.url || ''
    },
    mode: 'onSubmit'
  });
  const {
    t
  } = useTranslation(['build', 'common']);

  /**
   * URL 검증 및 영상 정보 조회 함수
   */
  const handleConfirmUrl = async () => {
    const url = getValues('url');
    if (!url) {
      alertDialog.open({
        header: {
          title: t('build:build.ai.youtube.error.emptyUrl')
        }
      });
      return;
    }
    const result = validateYoutubeUrl(url);
    if (!result.isValid || !result.videoId) {
      alertDialog.open({
        header: {
          title: t('build:build.ai.youtube.error.invalidUrl')
        }
      });
      return;
    }
    onSearch(result.videoId, url);
    reset({
      url
    });
  };
  const handleCancel = usePreservedCallback(() => {
    reset({
      url: ''
    });
    onCancel();
  });
  useEffectOnce(() => {
    // 모달이 열리고 100ms 후에 텍스트 필드에 포커스
    setTimeout(() => {
      setFocus('url');
    }, 100);
  });
  return <TextField.Root required data-sentry-element="unknown" data-sentry-component="YoutubeUrlTextField" data-sentry-source-file="YoutubeUrlTextField.tsx">
      <TextField.Input {...register('url')} intent="strong" placeholder={t('build:build.ai.youtube.urlPlaceholder')} onKeyDown={e => {
      if (e.key === 'Enter') {
        void handleConfirmUrl();
      }
    }} autoFocus leftSlot={<YoutubeFillIcon size="md" className="shrink-0" fill={ICON_COLOR.destructive} />} rightSlot={<div className="flex flex-row items-center gap-4xs">
            <IconButton onClick={handleCancel} className={cn(!videoData && 'hidden')} size="md" variant={'transparent'} intent={'neutral'}>
              <XIcon size="md" />
            </IconButton>
            <Button size="sm" variant="outlined" intent="neutral" className={cn(!formState.isDirty && !!watch('url') && 'hidden')} disabled={!watch('url')} onClick={handleConfirmUrl}>
              {t('common:global.confirm.button')}
            </Button>
          </div>} data-sentry-element="unknown" data-sentry-source-file="YoutubeUrlTextField.tsx" />
    </TextField.Root>;
};